import { cn } from './cn.js';

export type ButtonVariant = 'primary' | 'accent' | 'soft' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'xs' | 'sm' | 'md';

const BASE =
  'inline-flex cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-40 active:scale-[0.98] outline-none';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-herb-600 text-white shadow-xs hover:bg-herb-700 active:bg-herb-800 focus:ring-2 focus:ring-herb-500/50 focus-visible:ring-2 focus-visible:ring-herb-500/50',
  accent:
    'bg-ink text-white shadow-xs hover:bg-ink-soft active:bg-ink focus:ring-2 focus:ring-ink/30 focus-visible:ring-2 focus-visible:ring-ink/30',
  soft:
    'border border-border bg-card text-ink shadow-xs hover:bg-cream hover:border-border-strong active:bg-sand focus:ring-2 focus:ring-herb-500/30 focus-visible:ring-2 focus-visible:ring-herb-500/30',
  ghost:
    'text-ink-soft hover:bg-cream hover:text-ink active:bg-sand focus:ring-2 focus:ring-herb-500/30 focus-visible:ring-2 focus-visible:ring-herb-500/30',
  danger:
    'bg-rust-600 text-white shadow-xs hover:bg-rust-700 active:bg-rust-700 focus:ring-2 focus:ring-rust-500/40 focus-visible:ring-2 focus-visible:ring-rust-500/40',
  outline:
    'border border-border-strong bg-transparent text-ink hover:bg-cream active:bg-sand focus:ring-2 focus:ring-herb-500/30 focus-visible:ring-2 focus-visible:ring-herb-500/30',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs',
  sm: 'h-8 px-3.5 text-xs sm:text-[13px]',
  md: 'h-10 px-4 text-sm',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: React.ReactNode;
  readonly ref?: React.Ref<HTMLButtonElement>;
}

export function Button({
  type = 'button',
  variant = 'soft',
  size = 'sm',
  className,
  icon,
  children,
  ref,
  ...rest
}: ButtonProps): React.ReactElement {
  return (
    <button ref={ref} type={type} {...rest} className={cn(BASE, VARIANTS[variant], SIZES[size], className)}>
      {icon}
      {children}
    </button>
  );
}

export interface LinkButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: React.ReactNode;
  readonly ref?: React.Ref<HTMLAnchorElement>;
}

export function LinkButton({
  variant = 'soft',
  size = 'xs',
  className,
  icon,
  children,
  ref,
  ...rest
}: LinkButtonProps): React.ReactElement {
  return (
    <a ref={ref} {...rest} className={cn(BASE, VARIANTS[variant], SIZES[size], className)}>
      {icon}
      {children}
    </a>
  );
}
